import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Download, RotateCcw, DatabaseBackup } from "lucide-react";
import api from "../../lib/api";
import { inputCls, Field, Panel, btnPrimary, btnSecondary, errMsg } from "./ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import MaintenanceSchedule from "./MaintenanceSchedule";
import BackupUpload from "./BackupUpload";

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

function DangerDialog({ action, onClose, onDone }) {
  const [form, setForm] = useState({ password: "", code: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const restore = action?.type === "restore";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { password: form.password, code: form.code, ...(restore ? { confirm: form.confirm } : {}) };
      if (restore) {
        await api.post(`/admin/backups/${action.backup.id}/restore`, body);
        toast.success("Data berhasil dikembalikan. Silakan login ulang.");
        setTimeout(() => window.location.assign("/login"), 1500);
      } else {
        const r = await api.post(`/admin/backups/${action.backup.id}/download`, body, { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url; a.download = `mql-backup-${action.backup.created_at.slice(0, 10)}-${action.backup.id.slice(0, 8)}.mqlbak`; a.click();
        URL.revokeObjectURL(url);
        toast.success("Backup diunduh");
      }
      onDone();
    } catch (err) {
      const d = err?.response?.data;
      let msg = errMsg(err);
      if (d instanceof Blob) {
        try { msg = JSON.parse(await d.text()).detail || msg; } catch { msg = "Gagal mengunduh backup"; }
      }
      toast.error(typeof msg === "string" ? msg : "Gagal mengunduh backup");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!action} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="danger-dialog" className="bg-[#202127] border-[#35353B] text-[#F5F3ED] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldAlert className="text-[#F06464]" size={20} />{restore ? "Restore Database" : "Unduh Backup"}</DialogTitle>
          <DialogDescription className="text-[#A8A8AD]">
            {restore ? "SEMUA data saat ini akan diganti dengan isi backup ini. Backup pengaman otomatis dibuat dulu sebelum restore." : "File backup terenkripsi dan hanya bisa dipulihkan lewat panel ini."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Password Owner"><input data-testid="danger-password-input" type="password" required className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
          <Field label="Kode Google Authenticator"><input data-testid="danger-code-input" required inputMode="numeric" pattern="\d{6}" maxLength={6} className={inputCls} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
          {restore && <Field label='Ketik "RESTORE" untuk konfirmasi'><input data-testid="danger-confirm-input" required className={inputCls} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} /></Field>}
          <button data-testid="danger-submit-btn" type="submit" disabled={busy || (restore && form.confirm !== "RESTORE")}
            className={restore ? "w-full bg-[#F06464] text-white font-bold rounded-sm py-2.5 disabled:opacity-40" : `${btnPrimary} w-full`}>
            {busy ? "Memproses..." : restore ? "Kembalikan Data" : "Unduh"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function MaintenanceTab() {
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const load = () => api.get("/admin/maintenance").then((r) => { setData(r.data); setMessage(r.data.message); });
  useEffect(() => {
    load();
    window.addEventListener("mql:maintenance-refresh", load);
    return () => window.removeEventListener("mql:maintenance-refresh", load);
  }, []);

  const toggle = async () => {
    if (!data.enabled && !window.confirm("Aktifkan Mode Maintenance? Pembeli & supplier tidak bisa membuka situs. Backup otomatis dibuat dulu.")) return;
    setBusy(true);
    try {
      const r = await api.put("/admin/maintenance", { enabled: !data.enabled, message });
      toast.success(r.data.enabled ? "Maintenance aktif, backup tersimpan" : "Maintenance dimatikan, situs dibuka kembali");
      window.dispatchEvent(new Event("mql:maintenance-refresh"));
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const backupNow = async () => {
    setBusy(true);
    try { await api.post("/admin/backups"); toast.success("Backup dibuat"); load(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (!data) return <Panel testId="maintenance-panel"><p className="text-sm text-[#A8A8AD]">Memuat...</p></Panel>;
  return (
    <div className="space-y-6">
      <Panel title="Mode Maintenance" testId="maintenance-panel">
        <div data-testid="maintenance-status" className={`inline-block px-3 py-1 rounded-sm text-xs font-bold mb-4 ${data.enabled ? "bg-[#F06464] text-white" : "bg-[#35C78A]/15 text-[#35C78A]"}`}>
          {data.enabled ? `AKTIF sejak ${new Date(data.enabled_at).toLocaleString()} · ${data.enabled_by}` : "Situs normal (tidak maintenance)"}
        </div>
        <p className="text-xs text-[#A8A8AD] mb-4">Saat aktif, pembeli, supplier, dan pengunjung hanya melihat halaman maintenance dan tidak bisa login atau bertransaksi. Owner & Staff tetap bisa login dan bekerja. Backup otomatis dibuat setiap kali maintenance dinyalakan.</p>
        <Field label="Pesan untuk pengunjung (opsional)">
          <input data-testid="maintenance-message-input" maxLength={300} className={inputCls} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="MQL Group sedang maintenance. Silakan kembali beberapa saat lagi." />
        </Field>
        <button data-testid="maintenance-toggle-btn" onClick={toggle} disabled={busy}
          className={`mt-4 ${data.enabled ? btnPrimary : "bg-[#F06464] hover:bg-[#ff7b7b] text-white font-bold rounded-sm px-5 py-2.5 transition-colors disabled:opacity-40"}`}>
          {busy ? "Memproses..." : data.enabled ? "Matikan Maintenance (buka situs)" : "Aktifkan Maintenance + Backup"}
        </button>
      </Panel>

      <MaintenanceSchedule data={data} onChange={load} />

      <Panel title="Backup Database" testId="backups-panel">
        <p className="text-xs text-[#A8A8AD] mb-4">Backup disimpan terenkripsi. Unduh dan Restore wajib password + kode Authenticator. Restore hanya bisa saat maintenance aktif, dan sistem membuat backup pengaman dulu sebelum mengembalikan data. Punya file .mqlbak yang pernah diunduh? Unggah di sini, lalu klik Restore pada baris yang muncul.</p>
        <div className="flex flex-wrap gap-3 mb-4">
          <button data-testid="backup-now-btn" onClick={backupNow} disabled={busy} className={`${btnSecondary} flex items-center gap-2`}><DatabaseBackup size={16} /> Backup Sekarang</button>
          <BackupUpload onDone={(id) => { setHighlight(id); load(); }} />
        </div>
        {data.backups.map((b) => (
          <div key={b.id} data-testid={`backup-row-${b.id}`} className={`flex flex-col sm:flex-row sm:items-center gap-2 py-3 border-b border-[#35353B] text-sm ${highlight === b.id ? "bg-[#D4AF37]/10 px-2" : ""}`}>
            <div className="flex-1">
              <div className="text-[#F5F3ED]">{new Date(b.created_at).toLocaleString()} · {b.created_by}</div>
              <div className="text-xs text-[#A8A8AD]">{b.reason} · {fmtSize(b.size_bytes)} · {(b.counts.orders || 0)} pesanan, {(b.counts.users || 0)} pengguna, {(b.counts.products || 0)} produk</div>
            </div>
            <button data-testid={`backup-download-${b.id}`} onClick={() => setAction({ type: "download", backup: b })} className="flex items-center gap-1 text-[#D4AF37] hover:underline text-xs"><Download size={14} /> Unduh</button>
            <button data-testid={`backup-restore-${b.id}`} disabled={!data.enabled} title={data.enabled ? "" : "Aktifkan maintenance dulu"}
              onClick={() => setAction({ type: "restore", backup: b })} className="flex items-center gap-1 text-[#F06464] hover:underline text-xs disabled:opacity-40 disabled:no-underline"><RotateCcw size={14} /> Restore</button>
          </div>
        ))}
        {data.backups.length === 0 && <div className="text-sm text-[#A8A8AD]">Belum ada backup</div>}
      </Panel>
      <DangerDialog key={action ? `${action.type}-${action.backup.id}` : "none"} action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} />
    </div>
  );
}
